<template>
  <div class="hms-page">
    <Navigation />

    <div class="container-fluid hms-page-body">
      <!-- HEADER -->
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <div>
            <h4 class="hms-page-title m-0"><i class="mdi mdi-office-building-cog-outline me-2"></i>Department Overview</h4>
            <div class="hms-muted-sm">Patient flow, staffing &amp; workload per department — {{ data.period_label || 'today' }}</div>
          </div>
        </div>
        <div class="d-flex gap-2 align-items-center flex-wrap">
          <select class="hms-input hms-filter" v-model="branchFilter" @change="loadAll">
            <option value="">Filter by facility</option>
            <option v-for="b in branches" :key="b.branch_id" :value="b.branch_id">{{ b.branch_name }}</option>
          </select>
          <input class="hms-input hms-filter" v-model.trim="deptFilter" placeholder="Filter by department…" @keyup.enter="loadAll" @change="loadAll">
          <div class="hms-tabs hms-tabs-sm">
            <button class="hms-tab" :class="{ active: period==='today' }" @click="setPeriod('today')">Today</button>
            <button class="hms-tab" :class="{ active: period==='this_week' }" @click="setPeriod('this_week')">Week</button>
            <button class="hms-tab" :class="{ active: period==='this_month' }" @click="setPeriod('this_month')">Month</button>
          </div>
          <button class="hms-btn hms-btn-blue" @click="loadAll" :disabled="loading">
            <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i>
          </button>
          <button class="hms-btn hms-btn-blue" @click="printPage"><i class="mdi mdi-printer me-1"></i>Print</button>
        </div>
      </div>

      <div v-if="loading && !loaded" class="hms-spinner"><div class="spinner-border"></div></div>

      <div v-else class="hms-print-area">
        <!-- SUMMARY CARDS -->
        <div class="row g-3 mb-3">
          <div class="col-lg-3 col-md-6" v-for="c in summaryCards" :key="c.label">
            <div class="hms-do-card">
              <div class="hms-do-ic" :style="{ background: c.color + '18', color: c.color }"><i class="mdi" :class="c.icon"></i></div>
              <div>
                <div class="hms-do-num">{{ c.value }}</div>
                <div class="hms-do-lbl">{{ c.label }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="row g-3">
          <!-- QUEUE / SERVICE POINTS -->
          <div class="col-lg-7">
            <div class="hms-card mb-3">
              <div class="hms-card-head">Service Points — Queue Performance</div>
              <div class="hms-card-body p-0">
                <table class="hms-table" v-if="queueRows.length">
                  <thead>
                    <tr>
                      <th>Department</th>
                      <th class="text-end">Tickets</th>
                      <th class="text-end">Waiting</th>
                      <th class="text-end">In service</th>
                      <th class="text-end">Served</th>
                      <th class="text-end">No-show</th>
                      <th class="text-end">Avg wait</th>
                      <th class="text-end">Avg serve</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in queueRows" :key="r.queue_dept_id">
                      <td>
                        <span class="hms-do-dot" :style="{ background: r.color || '#1a7fe8' }"></span>
                        {{ r.dept_name }} <span class="hms-muted-sm" v-if="r.dept_code">({{ r.dept_code }})</span>
                      </td>
                      <td class="text-end fw-bold">{{ num(r.tickets_total) }}</td>
                      <td class="text-end" :class="{ 'hms-warn': Number(r.waiting) > 0 }">{{ num(r.waiting) }}</td>
                      <td class="text-end">{{ num(r.in_service) }}</td>
                      <td class="text-end">{{ num(r.served) }}</td>
                      <td class="text-end" :class="{ 'hms-danger': Number(r.no_show) > 0 }">{{ num(r.no_show) }}</td>
                      <td class="text-end">{{ mins(r.avg_wait_min) }}</td>
                      <td class="text-end">{{ mins(r.avg_serve_min) }}</td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="hms-muted text-center hms-pad">No active service points found.</div>
              </div>
            </div>

            <!-- VISITS BY DEPARTMENT -->
            <div class="hms-card">
              <div class="hms-card-head">Clinical Visits by Department</div>
              <div class="hms-card-body p-0">
                <table class="hms-table" v-if="visitRows.length">
                  <thead>
                    <tr>
                      <th>Department</th>
                      <th class="text-end">Visits</th>
                      <th class="text-end">Patients</th>
                      <th class="text-end">Active</th>
                      <th class="text-end">Completed</th>
                      <th class="text-end">Admitted</th>
                      <th class="text-end">Referred out</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in visitRows" :key="r.department">
                      <td>{{ r.department }}</td>
                      <td class="text-end fw-bold">{{ num(r.visits) }}</td>
                      <td class="text-end">{{ num(r.unique_patients) }}</td>
                      <td class="text-end">{{ num(r.active) }}</td>
                      <td class="text-end">{{ num(r.completed) }}</td>
                      <td class="text-end">{{ num(r.admitted) }}</td>
                      <td class="text-end">{{ num(r.referred_out) }}</td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="hms-muted text-center hms-pad">No visits in this period.</div>
              </div>
            </div>
          </div>

          <div class="col-lg-5">
            <!-- STAFFING -->
            <div class="hms-card mb-3">
              <div class="hms-card-head">Staffing by Department</div>
              <div class="hms-card-body p-0">
                <table class="hms-table" v-if="staffRows.length">
                  <thead>
                    <tr>
                      <th>Department</th>
                      <th class="text-end">Headcount</th>
                      <th class="text-end">Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in staffRows" :key="r.department">
                      <td>{{ r.department }}</td>
                      <td class="text-end fw-bold">{{ num(r.staff_count) }}</td>
                      <td class="text-end">{{ num(r.active_staff) }}</td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="hms-muted text-center hms-pad">No staff records.</div>
              </div>
            </div>

            <!-- REVENUE -->
            <div class="hms-card">
              <div class="hms-card-head">Billed Revenue by Service Category</div>
              <div class="hms-card-body p-0">
                <table class="hms-table" v-if="revRows.length">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th class="text-end">Lines</th>
                      <th class="text-end">Billed (KES)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in revRows" :key="r.category">
                      <td>{{ catLabel(r.category) }}</td>
                      <td class="text-end">{{ num(r.line_count) }}</td>
                      <td class="text-end fw-bold">{{ num(r.billed) }}</td>
                    </tr>
                    <tr>
                      <td class="fw-bold">Total</td>
                      <td></td>
                      <td class="text-end fw-bold">{{ num(revTotal) }}</td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="hms-muted text-center hms-pad">No billing lines in this period.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }
.hms-filter { max-width: 200px; }
.hms-spinner { display: flex; justify-content: center; padding: 60px 0; }

.hms-do-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; display: flex; align-items: center; gap: 12px; height: 100%; }
.hms-do-ic { width: 42px; height: 42px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 22px; flex-shrink: 0; }
.hms-do-num { font-size: 22px; font-weight: 700; color: #1a202c; line-height: 1.1; }
.hms-do-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: 0.4px; }
.hms-do-dot { display: inline-block; width: 9px; height: 9px; border-radius: 50%; margin-right: 6px; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 700; color: #2d3748; font-size: 14px; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }

.hms-table { width: 100%; }
.hms-table th, .hms-table td { padding: 8px 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; }
.hms-table th { background: #f7fafc; color: #4a5568; text-align: left; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.3px; }
.text-end { text-align: right; }
.fw-bold { font-weight: 700; }
.hms-warn   { color: #d97706; font-weight: 700; }
.hms-danger { color: #dc2626; font-weight: 700; }

.hms-input { padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 6px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-blue { background: #2b6cb0; color: #fff; }

.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }

.hms-tabs-sm { display: inline-flex; gap: 4px; }
.hms-tab { padding: 5px 12px; font-size: 12px; border-radius: 6px; background: #f7fafc; color: #718096; border: 1px solid #e2e8f0; cursor: pointer; font-weight: 600; }
.hms-tab.active { background: #2b6cb0; color: #fff; border-color: #2b6cb0; }

@media print {
  .hms-page-header .hms-btn, .hms-back, .hms-filter, .hms-tabs-sm { display: none !important; }
  .hms-card, .hms-do-card { break-inside: avoid; }
}
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "DepartmentOverview",
  components: { Navigation },
  data() {
    return {
      loading: true,
      loaded: false,
      period: 'today',
      branchFilter: "",
      deptFilter: "",
      branches: [],
      data: {}
    };
  },
  computed: {
    queueRows()  { return this.data.Queue   || []; },
    visitRows()  { return this.data.Visits  || []; },
    staffRows()  { return this.data.Staff   || []; },
    revRows()    { return this.data.Revenue || []; },
    revTotal()   { return this.revRows.reduce(function(s, r) { return s + (Number(r.billed) || 0); }, 0); },
    summaryCards() {
      var tickets = this.queueRows.reduce(function(s, r) { return s + (Number(r.tickets_total) || 0); }, 0);
      var waiting = this.queueRows.reduce(function(s, r) { return s + (Number(r.waiting) || 0); }, 0);
      var visits  = this.visitRows.reduce(function(s, r) { return s + (Number(r.visits) || 0); }, 0);
      return [
        { label: 'Queue tickets',   value: this.num(tickets), icon: 'mdi-ticket-confirmation-outline', color: '#2b6cb0' },
        { label: 'Now waiting',     value: this.num(waiting), icon: 'mdi-account-clock-outline',       color: '#d97706' },
        { label: 'Clinical visits', value: this.num(visits),  icon: 'mdi-stethoscope',                 color: '#7c3aed' },
        { label: 'Billed (KES)',    value: this.num(this.revTotal), icon: 'mdi-cash-multiple',         color: '#16a34a' }
      ];
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.loadBranches();
    this.loadAll();
  },
  methods: {
    setPeriod(p) { this.period = p; this.loadAll(); },
    async loadBranches() {
      try {
        var r = await executeJson({ function: "getBranches" }, hms);
        if (r.data && r.data.success) this.branches = (r.data.data && r.data.data.Branches) || [];
      } catch (e) {}
    },
    async loadAll() {
      this.loading = true;
      try {
        var payload = { function: "getDepartmentOverview", period: this.period };
        if (this.branchFilter) payload.branch_id  = this.branchFilter;
        if (this.deptFilter)   payload.department = this.deptFilter;
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) { this.data = r.data.data || {}; this.loaded = true; }
      } catch (e) { console.error(e); }
      this.loading = false;
    },
    printPage() { window.print(); },
    num(n) { if (n == null || n === '') return '0'; return Number(n).toLocaleString(); },
    mins(n) { if (n == null || n === '') return '—'; return Number(n).toLocaleString() + 'm'; },
    catLabel(c) {
      if (!c) return 'Uncategorised';
      return String(c).charAt(0).toUpperCase() + String(c).slice(1).replace(/_/g, ' ');
    }
  }
};
</script>
